// A single, permanently-mounted, visually-hidden live region per aria-live level.
// Status text toggled in/out via v-if/v-show mounts its role/aria-live container and
// its text in the same DOM update, which many screen readers don't reliably announce.
// Mutating the text content of a region that already existed before the change does.
//
// announce() clears the region now, then restores the message on a later task.
// Clearing and restoring in the same task lets a screen reader coalesce both
// mutations into one no-op, so a message announced twice in a row would go
// unannounced the second time.
const regions = { polite: null, assertive: null }
const restoreTimeoutIds = { polite: null, assertive: null }

function createRegion(id, ariaLive) {
  const el = document.createElement('div')
  el.id = id
  el.className = 'sr-only'
  el.setAttribute('role', ariaLive === 'assertive' ? 'alert' : 'status')
  el.setAttribute('aria-live', ariaLive)
  el.setAttribute('aria-atomic', 'true')
  document.body.appendChild(el)
  return el
}

export function mountLiveAnnouncer() {
  regions.polite = regions.polite || createRegion('live-announcer-polite', 'polite')
  regions.assertive = regions.assertive || createRegion('live-announcer-assertive', 'assertive')
}

export function announce(message, { assertive = false } = {}) {
  mountLiveAnnouncer()
  const level = assertive ? 'assertive' : 'polite'
  const region = regions[level]

  clearTimeout(restoreTimeoutIds[level])
  region.textContent = ''
  restoreTimeoutIds[level] = setTimeout(() => {
    region.textContent = message
  }, 0)
}

export function unmountLiveAnnouncerForTests() {
  clearTimeout(restoreTimeoutIds.polite)
  clearTimeout(restoreTimeoutIds.assertive)
  restoreTimeoutIds.polite = null
  restoreTimeoutIds.assertive = null
  regions.polite?.remove()
  regions.assertive?.remove()
  regions.polite = null
  regions.assertive = null
}
